import type { Feature, FeatureCollection, Point } from 'geojson'
import type { DriverSchedule, LocationInfo, PointsOfInterestResponse, RouteItem } from '../../types'
import * as turf from '@turf/turf'
import type { MapFilters, MapSettings, PoiTypes, PopoverInfo } from './types'
import { OFFLINE_THRESHOLD, ROUTE_ARROWS, ROUTE_COLORS, TURF_OPTIONS } from './constants'
import dayjs from 'dayjs'
import type { DriverTrackingState } from '../../stores'

export function getRouteProperties(index: number): {
  color: string
  arrow: string
} {
  return {
    color: ROUTE_COLORS[index % ROUTE_COLORS.length],
    arrow: ROUTE_ARROWS[index % ROUTE_ARROWS.length],
  }
}

function getBreadcrumbs(breadcrumbs: DriverSchedule['breadcrumbs'], timezone: string | undefined) {
  const orderGroups: Record<number, { coordinates: [number, number][]; points: FeatureCollection }> = {}

  // Group breadcrumbs by order number and assign colors
  breadcrumbs.forEach((crumb) => {
    if (!crumb.order_number) return // Skip if no order number

    if (!orderGroups[crumb.order_number]) {
      // Initialize the order group with a color and arrow
      orderGroups[crumb.order_number] = { coordinates: [], points: { type: 'FeatureCollection', features: [] } }
    }

    // Add coordinates to the order group
    orderGroups[crumb.order_number].coordinates.push([crumb.lon, crumb.lat])

    // Create a GeoJSON feature for each breadcrumb
    const properties = getBreadcrumbPopoverInfo(crumb, timezone)
    orderGroups[crumb.order_number].points.features.push(formatGeoJSONPoint(properties))
  })

  return orderGroups
}

function getRouteData(
  orderGroups: Record<number, { coordinates: [number, number][]; points: FeatureCollection }>,
  routeProperties: DriverTrackingState['driverOrdersOnRoute']
) {
  const routeData: Record<string, { line: FeatureCollection; points: FeatureCollection }> = {}

  Object.entries(orderGroups).forEach(([ordNum, orderGroup]) => {
    const orderNumber = parseInt(ordNum)
    routeData[orderNumber] = {
      line: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {
              lineColor: routeProperties[orderNumber]?.color || '#4285F4',
              lineWidth: 4,
              lineDasharray: [1],
              orderNumber: orderNumber,
            },
            geometry: {
              type: 'LineString',
              coordinates: orderGroup.coordinates,
            },
          },
        ],
      },
      points: orderGroup.points,
    }
  })

  return routeData
}

export function createRoutes(
  breadcrumbs: DriverSchedule['breadcrumbs'] | undefined,
  routeProperties: DriverTrackingState['driverOrdersOnRoute'],
  timezone: string | undefined
) {
  if (!breadcrumbs || breadcrumbs.length === 0) {
    return {}
  }

  const orderGroups = getBreadcrumbs(breadcrumbs, timezone)
  return getRouteData(orderGroups, routeProperties)
}

export function getGeoJSONPolygons(locationList: LocationInfo[]): FeatureCollection {
  const features = locationList.map((poi, index) => {
    if (poi.geofence.geo_json.geometry.type === 'Point') {
      return turf.circle(
        poi.geofence.geo_json.geometry.coordinates as [number, number],
        poi.geofence.geo_json.properties.radius_miles as number,
        {
          ...TURF_OPTIONS,
        }
      )
    }
    return {
      type: 'Feature' as const,
      properties: poi.geofence.geo_json.properties || {}, // Ensure properties exist
      geometry: {
        type: 'Polygon' as const,
        coordinates: poi.geofence.geo_json.geometry.coordinates as number[][][],
      },
    }
  })

  return {
    type: 'FeatureCollection' as const,
    features,
  }
}

export function getGeoJSONPoints(locationList: LocationInfo[]) {
  return {
    type: 'FeatureCollection' as const, // Use 'as const' for literal type
    features: locationList.map((poi, index) => {
      const properties = getPoiPopoverInfo(poi)
      return formatGeoJSONPoint(properties)
    }),
  }
}

export function getActionPinsGeoJSON(
  routeItems: RouteItem[] | undefined,
  timezone: string | undefined
): Record<string, FeatureCollection> {
  if (!routeItems || routeItems.length === 0) {
    return {}
  }
  const pinGroups: Record<string, FeatureCollection> = {}

  routeItems
    .filter((route) => route.action === 'loading' || route.action === 'dropping')
    .forEach((route) => {
      if (!route.order_number) return // Skip if no order number

      if (!pinGroups[route.order_number]) {
        pinGroups[route.order_number] = {
          type: 'FeatureCollection' as const,
          features: [] as Feature<Point>[],
        }
      }

      // Create Load and Drop pins
      if (route.manual_start_gps_lat && route.manual_start_gps_lon) {
        const properties = getLoadDropPopoverInfo(route, timezone)
        const geoJSON = formatGeoJSONPoint(properties)
        pinGroups[route.order_number].features.push(geoJSON)
      }
      // Create Load and Drop Arrival pins
      if (route.gps_start_event) {
        const properties = getArrivalDeparturePopoverInfo(route, 'arrival', timezone)
        const geoJSON = formatGeoJSONPoint(properties)
        pinGroups[route.order_number].features.push(geoJSON)
      }
      // Create Load and Drop Departure pins
      if (route.gps_end_event) {
        const properties = getArrivalDeparturePopoverInfo(route, 'departure', timezone)
        const geoJSON = formatGeoJSONPoint(properties)
        pinGroups[route.order_number].features.push(geoJSON)
      }
    })

  return pinGroups
}

function formatGeoJSONPoint<T>(properties: PopoverInfo) {
  return {
    type: 'Feature' as const,
    geometry: {
      type: 'Point' as const,
      coordinates: [properties.lon, properties.lat],
    },
    properties,
  }
}

function getBreadcrumbPopoverInfo(
  breadcrumb: DriverSchedule['breadcrumbs'][number],
  timezone: string | undefined
): PopoverInfo {
  return {
    popoverType: 'breadcrumb' as const,
    id: breadcrumb.order_number?.toString() || '',
    lat: breadcrumb.lat,
    lon: breadcrumb.lon,
    timestamp: breadcrumb.date,
    trigger: breadcrumb.trigger,
    timezone: timezone || '',
  }
}

function getPoiPopoverInfo(poi: LocationInfo): PopoverInfo {
  return {
    popoverType: 'poi',
    id: poi.location_id,
    lat: poi.lat,
    lon: poi.lon,
    location: poi.location_name,
    siteName: poi.site_name || null,
    locationType: poi.type,
    address: poi.address,
    city: poi.city,
    state: poi.state,
    postalCode: poi.postal_code || '',
    radius: poi.geofence.geo_json.properties?.radius_miles as number | undefined,
  }
}

function getLoadDropPopoverInfo(routeItem: RouteItem, timezone: string | undefined): PopoverInfo {
  let productMovements = '[]'
  try {
    productMovements = JSON.stringify(routeItem.product_movements) || '[]'
  } catch (error) {
    console.error('Error stringifying product movements:', error)
  }
  return {
    popoverType: 'load-drop',
    id: routeItem.order_number?.toString() || '',
    lat: routeItem.manual_start_gps_lat || 0,
    lon: routeItem.manual_start_gps_lon || 0,
    timestamp: routeItem.end_time || '',
    name: routeItem.destination_name,
    action: routeItem.action as 'loading' | 'dropping',
    productMovements,
    timezone: timezone || '',
  }
}

function getArrivalDeparturePopoverInfo(
  routeItem: RouteItem,
  event: 'arrival' | 'departure',
  timezone: string | undefined
): PopoverInfo {
  return {
    popoverType: 'arrival-departure',
    id: routeItem.order_number?.toString() || '',
    lat: (event === 'arrival' ? routeItem.gps_start_event?.lat : routeItem.gps_end_event?.lat) || 0,
    lon: (event === 'arrival' ? routeItem.gps_start_event?.lon : routeItem.gps_end_event?.lon) || 0,
    timestamp: (event === 'arrival' ? routeItem.start_time : routeItem.end_time) || '',
    locationName: routeItem.destination_name,
    action: routeItem.action as 'loading' | 'dropping',
    event,
    timezone: timezone || '',
  }
}

export function getDriverPopoverInfo(
  lastSentUpdate: DriverSchedule['last_update'],
  driverScheduleId: string,
  driverName: string,
  complete: boolean,
  timezone: string | undefined
): PopoverInfo {
  return {
    popoverType: 'driver',
    id: driverScheduleId,
    lat: lastSentUpdate?.lat || 0,
    lon: lastSentUpdate?.lon || 0,
    driverName: driverName,
    orderNumber: lastSentUpdate?.detail?.order_number?.toString() || '',
    action: lastSentUpdate?.detail?.action,
    lastHeartbeat: lastSentUpdate?.last_heartbeat,
    eta: lastSentUpdate?.detail?.projected_end_time,
    complete,
    timezone: timezone || '',
  }
}

export function showLivePulse(lastHeartbeat: string | null | undefined, complete: boolean): boolean {
  if (!lastHeartbeat || complete) return false // If no heartbeat, don't show pulse
  const lastUpdate = dayjs(lastHeartbeat)
  const now = dayjs()
  const diffMinutes = now.diff(lastUpdate, 'minute')
  return diffMinutes <= OFFLINE_THRESHOLD
}

export function filterPointsOfInterest(
  pointsOfInterest: PointsOfInterestResponse,
  pointsOfInterestOnRoute: PointsOfInterestResponse,
  mapFilters: MapFilters,
  showLocations: MapSettings['showLocations']
) {
  if (showLocations === 'none') {
    return {
      sites: [],
      terminals: [],
      depots: [],
    }
  }

  const shownPointsOfInterest = showLocations === 'only_on_route' ? pointsOfInterestOnRoute : pointsOfInterest

  return {
    sites: shownPointsOfInterest?.sites?.filter((poi) => passesMapFilters('sites', mapFilters, poi)) || [],
    terminals: shownPointsOfInterest?.terminals?.filter((poi) => passesMapFilters('terminals', mapFilters, poi)) || [],
    depots: shownPointsOfInterest?.depots?.filter((poi) => passesMapFilters('depots', mapFilters, poi)) || [],
  }
}

function passesMapFilters<T extends keyof PointsOfInterestResponse>(
  key: T,
  filters: MapFilters,
  poi: PointsOfInterestResponse[T][number]
) {
  const filterKeys = Object.keys(filters[key]) as (keyof (typeof filters)[T])[]
  return filterKeys.every((filterKey) => {
    const filterValues = filters[key][filterKey] as string[]
    if (filterValues.length === 0) return true // No filter applied for this key
    const poiValue = poi[filterKey as keyof typeof poi]
    if (typeof poiValue === 'string') {
      return filterValues.includes(poiValue)
    }
    return false
  })
}

export function getPointsOfInterestOnRoute(
  selectedDriverId: string | null,
  driverSchedule: DriverSchedule | null,
  allDriverTracking: DriverSchedule[],
  pointsOfInterest: PointsOfInterestResponse
) {
  let locationIdSet = new Set<string>()
  if (selectedDriverId && driverSchedule?.route) {
    locationIdSet = new Set(driverSchedule.route.map((step) => step.destination_id).filter(Boolean))
  } else {
    locationIdSet = new Set(
      allDriverTracking.flatMap((driver) => driver.route.map((step) => step.destination_id).filter(Boolean))
    )
  }
  return {
    sites: pointsOfInterest?.sites?.filter((poi) => locationIdSet.has(poi.location_id)) || [],
    terminals: pointsOfInterest?.terminals?.filter((poi) => locationIdSet.has(poi.location_id)) || [],
    depots: pointsOfInterest?.depots?.filter((poi) => locationIdSet.has(poi.location_id)) || [],
  }
}

export function getUniqueOptions(
  pointsOfInterest: PointsOfInterestResponse[PoiTypes],
  key: keyof PointsOfInterestResponse[PoiTypes][number]
) {
  if (!pointsOfInterest || !Array.isArray(pointsOfInterest)) {
    return []
  }
  const uniqueSet = new Set()
  pointsOfInterest.forEach((poi) => {
    if (poi[key]) {
      uniqueSet.add(poi[key])
    }
  })
  return Array.from(uniqueSet)
    .filter(Boolean)
    .map((item) => ({
      label: item,
      value: item,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function getSearchOptions(pointsOfInterest: PointsOfInterestResponse) {
  return {
    sites: {
      search:
        pointsOfInterest?.sites
          ?.map((poi) => ({
            label: `${poi.location_name} ${poi.site_name} - ${poi.counterparty_name}`,
            coords: [poi.lon, poi.lat],
            value: poi.location_id,
            locationName: poi.location_name,
            siteName: poi.site_name,
            counterparty: poi.counterparty_name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || [],
      counterparty_name: getUniqueOptions(pointsOfInterest.sites, 'counterparty_name') || [],
      customer_name: getUniqueOptions(pointsOfInterest.sites, 'customer_name') || [],
      sector: getUniqueOptions(pointsOfInterest.sites, 'sector') || [],
      city: getUniqueOptions(pointsOfInterest.sites, 'city') || [],
      state: getUniqueOptions(pointsOfInterest.sites, 'state') || [],
      postal_code: getUniqueOptions(pointsOfInterest.sites, 'postal_code') || [],
    },
    terminals: {
      search:
        pointsOfInterest?.terminals
          ?.map((poi) => ({
            label: poi.location_name,
            coords: [poi.lon, poi.lat],
            value: poi.location_id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || [],
      sector: getUniqueOptions(pointsOfInterest.terminals, 'sector') || [],
      city: getUniqueOptions(pointsOfInterest.terminals, 'city') || [],
      state: getUniqueOptions(pointsOfInterest.terminals, 'state') || [],
      postal_code: getUniqueOptions(pointsOfInterest.terminals, 'postal_code') || [],
    },
    depots: {
      search:
        pointsOfInterest?.depots
          ?.map((poi) => ({
            label: poi.location_name,
            coords: [poi.lon, poi.lat],
            value: poi.location_id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)) || [],
      sector: getUniqueOptions(pointsOfInterest.depots, 'sector') || [],
      city: getUniqueOptions(pointsOfInterest.depots, 'city') || [],
      state: getUniqueOptions(pointsOfInterest.depots, 'state') || [],
      postal_code: getUniqueOptions(pointsOfInterest.depots, 'postal_code') || [],
    },
  }
}

export function isFilterActive(filters: MapFilters, poiTypeKey: 'terminals' | 'depots' | 'sites'): boolean {
  if (!filters || !filters[poiTypeKey]) {
    return false
  }
  const poiFilterKeys = Object.keys(filters[poiTypeKey]) as Array<keyof (typeof filters)[typeof poiTypeKey]>
  return poiFilterKeys.some((key) => (filters[poiTypeKey] as Record<string, string[]>)[key]?.length > 0)
}
